<template>
  <div class="rf-lotes">
    <div ref="mapa" class="rf-lotes-globo"></div>

    <div class="rf-lotes-controles">
      <button type="button" title="Acercar" aria-label="Acercar" :disabled="!listo" @click="zoom(true)">+</button>
      <button type="button" title="Alejar" aria-label="Alejar" :disabled="!listo" @click="zoom(false)">−</button>
      <button type="button" title="Repetir animación" aria-label="Repetir animación" :disabled="!listo" @click="animar">↻</button>
    </div>

    <button
      type="button"
      class="rf-lotes-norte"
      title="Orientar al norte"
      aria-label="Orientar al norte"
      :disabled="!listo"
      @click="alNorte"
    >
      <span>N</span>
      <svg viewBox="0 0 20 20" :style="{ transform: `rotate(${-rumbo}rad)` }" aria-hidden="true">
        <path d="M10 2 L15.5 17 L10 13.5 L4.5 17 Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" />
      </svg>
    </button>

    <div v-if="lote" class="rf-lotes-popup" role="dialog" :aria-label="`Lote ${lote.numero}`">
      <div class="rf-lotes-popup-cabeza">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 19c9 0 14-6 14-14C11 5 5 10 5 19Zm0 0 7-7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <strong>Lote {{ lote.numero }}</strong>
        <span class="rf-lotes-estado" :class="`rf-lotes-estado--${lote.estado}`">{{ estadoTexto(lote.estado) }}</span>
        <button type="button" class="rf-lotes-cerrar" aria-label="Cerrar" @click="seleccionar(null)">✕</button>
      </div>
      <dl>
        <div>
          <dt>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h16v16H4z" fill="none" stroke="currentColor" stroke-width="1.6" /></svg>
            Superficie
          </dt>
          <dd>{{ superficie(lote.superficie_m2) }}</dd>
        </div>
        <div v-if="lote.ubicacion">
          <dt>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Zm0-9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" fill="none" stroke="currentColor" stroke-width="1.6" /></svg>
            Ubicación
          </dt>
          <dd>{{ lote.ubicacion }}</dd>
        </div>
        <div v-if="lote.entorno">
          <dt>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 6 12h3l-4 6h14l-4-6h3Zm0 15v3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" /></svg>
            Entorno
          </dt>
          <dd>{{ lote.entorno }}</dd>
        </div>
      </dl>
      <a
        v-if="whatsapp"
        class="rf-lotes-whatsapp"
        :href="whatsapp"
        target="_blank"
        rel="noopener"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3a9 9 0 0 0-7.8 13.5L3 21l4.6-1.2A9 9 0 1 0 12 3Zm4.6 12.6c-.2.6-1.1 1.1-1.6 1.2-.4.1-1 .1-1.6-.1a14 14 0 0 1-1.5-.6 11.6 11.6 0 0 1-4.4-3.9 5 5 0 0 1-1-2.7c0-1.3.7-1.9.9-2.2.3-.3.6-.3.8-.3h.6c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.3.5-.4.5c-.1.1-.3.3-.1.6.2.3.7 1.2 1.6 1.9 1.1 1 2 1.3 2.3 1.4.3.1.4.1.6-.1l.9-1c.2-.3.4-.2.6-.1l1.9.9c.3.1.5.2.5.3.1.1.1.6-.1 1.2Z" fill="currentColor" />
        </svg>
        Contactar por WhatsApp
        <span aria-hidden="true">→</span>
      </a>
      <p v-else class="rf-lotes-sin-whatsapp">El contacto por WhatsApp no está disponible.</p>
    </div>

    <div v-else-if="listo && !error" class="rf-lotes-pildora">
      <strong>{{ proyecto.nombre }}</strong>
      <span>{{ dibujado ? 'Pasa el mouse sobre un lote' : 'Llegando al proyecto…' }}</span>
    </div>

    <div class="rf-lotes-leyenda">
      <span><i class="rf-lotes-punto rf-lotes-punto--disponible"></i> Disponible</span>
      <span><i class="rf-lotes-punto rf-lotes-punto--reservado"></i> Reservado</span>
    </div>

    <p v-if="error" class="rf-lotes-error">{{ error }}</p>
  </div>
</template>

<script>
// El vuelo es copia del de CommandMap a propósito: la landing es otro diseño y
// se espera que diverja, así que sólo comparte el loader y la geometría.
import { Caja, cargarCesium, pausa, proveedorBase, recorrerCoordenadas } from '@/services/cesium';
import { superficie } from './formato';

const EUROPA = { lng: 10, lat: 48 };
const ALTURA_EUROPA = 7000000;
const ALTURA_MUNDO = 18000000;
const VUELO_ALEJAR = 2.2;
const VUELO_CRUZAR = 3.2;
const VUELO_CAER = 4.0;
const COLORES = {
  seleccionado: ['#2e8b6a', 0.62],
  reservado: ['#8c9a95', 0.5],
  // Casi transparente pero no del todo: así el polígono se puede apuntar.
  disponible: ['#ffffff', 0.05],
};

// La insignia redonda con el número del lote, dibujada una vez en un canvas.
function insignia(numero) {
  const lado = 64;
  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const ctx = canvas.getContext('2d');
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 6;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(lado / 2, lado / 2, lado / 2 - 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#07394f';
  ctx.font = `600 ${String(numero).length > 2 ? 18 : 24}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(numero), lado / 2, lado / 2 + 1);
  return canvas;
}

export default {
  name: 'MapaLotes',
  props: {
    proyecto: { type: Object, required: true },
    whatsappNumero: { type: String, default: '' },
    whatsappMensaje: { type: String, default: '' },
  },
  data() {
    return { listo: false, dibujado: false, error: '', lote: null, rumbo: 0 };
  },
  computed: {
    whatsapp() {
      if (!this.whatsappNumero || !this.lote) return '';
      const texto = this.whatsappMensaje
        .replace('{lote}', `Lote ${this.lote.numero}`)
        .replace('{proyecto}', this.proyecto.nombre);
      return `https://wa.me/${this.whatsappNumero}?text=${encodeURIComponent(texto)}`;
    },
  },
  created() {
    // Fuera de `data`: el proxy reactivo de Vue rompe el visor de Cesium.
    this.visor = null;
    this.manejador = null;
    this.caja = null;
    this.piezas = {};
    this.avance = 0;
    this.corrida = 0;
  },
  async mounted() {
    window.addEventListener('keydown', this.tecla);
    try {
      await this.iniciar();
      this.listo = true;
      this.animar();
    } catch (e) {
      this.error = e.message || 'No se pudo cargar el mapa.';
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.tecla);
    this.corrida += 1;
    if (this.manejador && !this.manejador.isDestroyed()) this.manejador.destroy();
    if (this.visor && !this.visor.isDestroyed()) this.visor.destroy();
    this.visor = null;
  },
  methods: {
    superficie,

    estadoTexto(estado) {
      return estado === 'reservado' ? 'Reservado' : 'Disponible';
    },

    async iniciar() {
      const caja = new Caja();
      this.proyecto.lotes.forEach((lote) => {
        ((lote.geojson && lote.geojson.features) || []).forEach((f) => {
          recorrerCoordenadas(f.geometry, (lng, lat) => caja.extender(lng, lat));
        });
      });
      if (caja.vacia) throw new Error('Este proyecto todavía no tiene lotes cargados.');
      this.caja = caja;

      const Cesium = await cargarCesium();
      this.visor = new Cesium.Viewer(this.$refs.mapa, {
        animation: false,
        timeline: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        baseLayerPicker: false,
        infoBox: false,
        selectionIndicator: false,
        fullscreenButton: false,
        baseLayer: new Cesium.ImageryLayer(proveedorBase(Cesium, 'satelital')),
      });
      this.visor.camera.percentageChanged = 0.01;
      this.visor.camera.changed.addEventListener(() => {
        this.rumbo = this.visor.camera.heading;
      });

      for (const lote of this.proyecto.lotes) {
        await this.dibujarLote(Cesium, lote);
      }
      this.pintar(0);
      this.engancharMouse(Cesium);
    },

    async dibujarLote(Cesium, lote) {
      const pieza = { lote, rellenos: [], bordes: [], insignia: null };
      this.piezas[lote.id] = pieza;
      const fuente = await Cesium.GeoJsonDataSource.load(lote.geojson, { clampToGround: true });
      this.visor.dataSources.add(fuente);

      fuente.entities.values.slice().forEach((entidad) => {
        if (entidad.billboard) entidad.billboard = undefined;
        if (!entidad.polygon) return;
        entidad.polygon.outline = false;
        entidad.rfLote = lote.id;
        pieza.rellenos.push(entidad);
        // Pegado al suelo Cesium no dibuja el outline: va como polilínea.
        const jerarquia = entidad.polygon.hierarchy.getValue(Cesium.JulianDate.now());
        [jerarquia.positions, ...(jerarquia.holes || []).map((h) => h.positions)]
          .filter((anillo) => anillo && anillo.length > 1)
          .forEach((anillo) => {
            const borde = fuente.entities.add({
              polyline: { positions: anillo.concat([anillo[0]]), width: 2.5, clampToGround: true },
            });
            borde.rfLote = lote.id;
            pieza.bordes.push(borde);
          });
      });

      const caja = new Caja();
      lote.geojson.features.forEach((f) => recorrerCoordenadas(f.geometry, (lng, lat) => caja.extender(lng, lat)));
      pieza.insignia = this.visor.entities.add({
        position: Cesium.Cartesian3.fromDegrees(caja.centro.lng, caja.centro.lat),
        billboard: {
          image: insignia(lote.numero),
          width: 30,
          height: 30,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: false,
        },
      });
      pieza.insignia.rfLote = lote.id;
    },

    // Basta pasar el mouse para abrir el popup, y se queda abierto para poder
    // llegar al botón; un clic fuera de los lotes (o Esc) lo cierra.
    engancharMouse(Cesium) {
      const lienzo = this.visor.scene.canvas;
      this.manejador = new Cesium.ScreenSpaceEventHandler(lienzo);
      this.manejador.setInputAction((mov) => {
        const id = this.loteEn(mov.endPosition);
        lienzo.style.cursor = id ? 'pointer' : '';
        if (id && (!this.lote || this.lote.id !== id)) this.seleccionar(this.piezas[id].lote);
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      this.manejador.setInputAction((clic) => {
        const id = this.loteEn(clic.position);
        this.seleccionar(id ? this.piezas[id].lote : null);
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    },

    loteEn(posicion) {
      if (!this.dibujado) return null;
      const elegido = this.visor.scene.pick(posicion);
      return (elegido && elegido.id && elegido.id.rfLote) || null;
    },

    seleccionar(lote) {
      this.lote = lote;
      this.pintar(this.avance);
    },

    tecla(evento) {
      if (evento.key === 'Escape' && this.lote) this.seleccionar(null);
    },

    pintar(avance) {
      const Cesium = window.Cesium;
      this.avance = avance;
      Object.values(this.piezas).forEach(({ lote, rellenos, bordes }) => {
        const elegido = this.lote && this.lote.id === lote.id;
        const [color, alfa] = COLORES[elegido ? 'seleccionado' : lote.estado] || COLORES.disponible;
        const relleno = Cesium.Color.fromCssColorString(color).withAlpha(alfa * avance);
        rellenos.forEach((e) => { e.polygon.material = relleno; });
        const borde = new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.WHITE.withAlpha(avance),
          dashLength: 14,
        });
        bordes.forEach((e) => {
          e.polyline.material = borde;
          e.polyline.width = elegido ? 3.5 : 2.5;
        });
      });
    },

    mostrarInsignias(visibles) {
      Object.values(this.piezas).forEach(({ insignia: i }) => { i.billboard.show = visibles; });
    },

    // Parte sobre Europa, se aleja al globo entero, cruza hasta el proyecto y cae
    // en vertical. Cada corrida invalida la anterior para poder repetirla.
    async animar() {
      const Cesium = window.Cesium;
      if (!this.visor) return;
      const corrida = ++this.corrida;
      const vigente = () => corrida === this.corrida && this.visor;
      const cenital = { heading: 0, pitch: Cesium.Math.toRadians(-90), roll: 0 };
      const destino = this.caja.centro;

      this.visor.camera.cancelFlight();
      this.dibujado = false;
      this.seleccionar(null);
      this.pintar(0);
      this.mostrarInsignias(false);
      this.visor.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(EUROPA.lng, EUROPA.lat, ALTURA_EUROPA),
        orientation: cenital,
      });

      await pausa(900);
      if (!vigente()) return;
      await this.vuelo({
        destination: Cesium.Cartesian3.fromDegrees(EUROPA.lng, EUROPA.lat, ALTURA_MUNDO),
        orientation: cenital,
        duration: VUELO_ALEJAR,
      });
      if (!vigente()) return;
      await this.vuelo({
        destination: Cesium.Cartesian3.fromDegrees(destino.lng, destino.lat, ALTURA_MUNDO),
        orientation: cenital,
        duration: VUELO_CRUZAR,
      });
      await pausa(400);
      if (!vigente()) return;
      await this.vuelo({ destination: this.caja.rectangulo(Cesium, 0.6), duration: VUELO_CAER });
      if (!vigente()) return;
      this.mostrarInsignias(true);
      await this.aparecer(corrida);
      if (vigente()) this.dibujado = true;
    },

    vuelo(opciones) {
      return new Promise((resolve) => {
        this.visor.camera.flyTo({ ...opciones, complete: resolve, cancel: resolve });
      });
    },

    aparecer(corrida, duracion = 900) {
      return new Promise((resolve) => {
        const inicio = performance.now();
        const paso = (ahora) => {
          if (corrida !== this.corrida || !this.visor) return resolve();
          const avance = Math.min(1, (ahora - inicio) / duracion);
          this.pintar(avance);
          if (avance < 1) requestAnimationFrame(paso);
          else resolve();
        };
        requestAnimationFrame(paso);
      });
    },

    zoom(acercar) {
      const camara = this.visor.camera;
      const altura = camara.positionCartographic.height;
      if (acercar) camara.zoomIn(altura * 0.4);
      else camara.zoomOut(altura * 0.6);
    },

    alNorte() {
      const camara = this.visor.camera;
      camara.flyTo({
        destination: camara.positionWC.clone(),
        orientation: { heading: 0, pitch: camara.pitch, roll: 0 },
        duration: 0.6,
      });
    },
  },
};
</script>

<style>
.refugios .rf-lotes {
  position: relative;
  height: min(620px, 72vh);
  min-height: 420px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--azul);
}
.refugios .rf-lotes-globo { position: absolute; inset: 0; }
.refugios .rf-lotes-controles {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.refugios .rf-lotes-controles button,
.refugios .rf-lotes-norte {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: rgba(7,57,79,0.92);
  color: var(--white);
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0,0,0,0.25);
  transition: background .2s;
}
.refugios .rf-lotes-controles button:hover, .refugios .rf-lotes-norte:hover { background: var(--teal); }
.refugios .rf-lotes-controles button:disabled, .refugios .rf-lotes-norte:disabled { opacity: .5; cursor: default; }
.refugios .rf-lotes-norte {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 42px;
  height: 52px;
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
}
.refugios .rf-lotes-norte span { font-size: 0.6rem; font-weight: 600; }
.refugios .rf-lotes-norte svg { width: 18px; height: 18px; transition: transform .1s linear; }

.refugios .rf-lotes-popup {
  position: absolute;
  left: 16px;
  bottom: 40px;
  width: min(330px, calc(100% - 32px));
  padding: 16px 18px 18px;
  border-radius: 14px;
  background: rgba(7,48,52,0.94);
  color: var(--white);
  box-shadow: 0 14px 40px rgba(0,0,0,0.35);
  border: 1px solid rgba(255,255,255,0.08);
}
.refugios .rf-lotes-popup svg { width: 16px; height: 16px; flex: none; }
.refugios .rf-lotes-popup-cabeza { display: flex; align-items: center; gap: 10px; padding-bottom: 12px; border-bottom: 1px solid rgba(255,255,255,0.12); }
.refugios .rf-lotes-popup-cabeza svg { width: 20px; height: 20px; color: rgba(255,255,255,0.8); }
.refugios .rf-lotes-popup-cabeza strong { font-size: 1.05rem; font-weight: 600; flex: 1; }
.refugios .rf-lotes-estado {
  font-size: 0.66rem;
  padding: 3px 10px 3px 8px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.25);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.refugios .rf-lotes-estado::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: var(--verde); }
.refugios .rf-lotes-estado--reservado::before { background: var(--reservado); }
.refugios .rf-lotes-cerrar { background: none; border: none; color: rgba(255,255,255,0.6); cursor: pointer; font-size: 0.85rem; }
.refugios .rf-lotes-cerrar:hover { color: var(--white); }
.refugios .rf-lotes-popup dl { margin: 12px 0 14px; display: grid; gap: 8px; }
.refugios .rf-lotes-popup dl > div { display: grid; grid-template-columns: 112px 1fr; gap: 8px; font-size: 0.8rem; }
.refugios .rf-lotes-popup dt { display: flex; align-items: center; gap: 8px; color: rgba(255,255,255,0.6); }
.refugios .rf-lotes-popup dd { color: var(--white); }
.refugios .rf-lotes-whatsapp {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 30px;
  background: linear-gradient(90deg, #1fa463, #25c26e);
  color: var(--white);
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: filter .2s;
}
.refugios .rf-lotes-whatsapp:hover { filter: brightness(1.08); }
.refugios .rf-lotes-whatsapp svg { width: 22px; height: 22px; }
.refugios .rf-lotes-whatsapp span { margin-left: auto; font-size: 1rem; }
.refugios .rf-lotes-sin-whatsapp { font-size: 0.78rem; color: rgba(255,255,255,0.65); font-style: italic; }

.refugios .rf-lotes-pildora {
  position: absolute;
  left: 16px;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 22px;
  border-radius: 30px;
  background: rgba(7,48,52,0.92);
  color: var(--white);
}
.refugios .rf-lotes-pildora strong { font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 600; }
.refugios .rf-lotes-pildora span { font-size: 0.66rem; color: rgba(255,255,255,0.65); letter-spacing: 0.06em; }

.refugios .rf-lotes-leyenda {
  position: absolute;
  right: 16px;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(7,48,52,0.88);
  color: var(--white);
  font-size: 0.72rem;
}
.refugios .rf-lotes-leyenda span { display: flex; align-items: center; gap: 8px; }
.refugios .rf-lotes-punto { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.refugios .rf-lotes-punto--disponible { background: var(--verde); }
.refugios .rf-lotes-punto--reservado { background: var(--reservado); }
.refugios .rf-lotes-error {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-width: calc(100% - 48px);
  padding: 14px 22px;
  border-radius: 14px;
  background: rgba(255,255,255,0.95);
  color: #8a1c1c;
  font-size: 0.88rem;
  text-align: center;
}
/* bottom: 40px deja a la vista la atribución de los mapas, que Cesium pone abajo. */
@media (max-width: 640px) {
  .refugios .rf-lotes { height: 520px; }
  .refugios .rf-lotes-leyenda { top: 76px; bottom: auto; }
  .refugios .rf-lotes-popup dl > div { grid-template-columns: 96px 1fr; }
}
</style>
